import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CheckBox, controlToastState, Modal } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { useInitialDataOfSingleMedicalOrder } from "../../../context/initialDataOfSingleMedicalOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import { EVWriteBackStatus } from "../../../enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { ModalId } from "../../../enums/modalId";
import { useLogJCodeStatusEvents } from "../../../hooks/logJCodeStatusEvents";
import { getAllJCodes } from "../../../pages/singleOrderLayout/utils/existingDocsToLs";
import { RootState } from "../../../redux/store/store";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import BadgeDropdown from "../../badgeDropdown/badgeDropdown";
import { generateWriteBackDataForManualUpdate } from "./utils/boValuesModalHelper";

const BoValuesModal = (props: { onClose: () => void }) => {
  const [comments, setComments] = useState<string>("");
  const [radioButtonsFlipped, setRadioButtonsFlipped] =
    useState<boolean>(false);
  const [boNotesAdded, setBoNotesAdded] = useState<boolean>(false);
  const [selectedBoValue, setSelectedBoValue] =
    useState<BadgeWithIconProps | null>(null);
  const [selectedAuthStatus, setSelectedAuthStatus] =
    useState<BadgeWithIconProps | null>(null);
  // Store old values for comparison
  const [oldAuthStatus, setOldAuthStatus] = useState<string>("");
  const [oldBoValue, setOldBoValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadedDocument, setUploadedDocument] = useState<boolean>(false);

  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );

  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { oldAuthStatus: oldAuthStatusFromContext } =
    useInitialDataOfSingleMedicalOrder();

  const navigate = useNavigate();

  const logJCodeStatusEvents = useLogJCodeStatusEvents();

  const isEventLogged = useRef(false);

  // Transform boOptions to match BadgeWithIconProps interface
  const transformedBoOptions: BadgeWithIconProps[] = useMemo(
    () =>
      (boOptions || []).map((option, index) => ({
        text: option.label,
        id: option.value,
        bgColor: "#F5F5F5",
        textColor: "#0F0F0F",
      })),
    [boOptions],
  );

  const shouldManuallyUploadCigNotes = useMemo(() => {
    return (
      singleMedicalPaOrder?.status?.ev_write_back_status ===
        EVWriteBackStatus.NOT_INITIATED ||
      singleMedicalPaOrder?.status?.ev_write_back_status ===
        EVWriteBackStatus.ERROR
    );
  }, [singleMedicalPaOrder]);

  const jCodes =
    getItemFromLocalStorage(
      `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
    ) ?? [];
  const jCode = jCodes.join(",");

  // Set default auth status and bo_values based on singleMedicalPaOrder when modal opens
  useEffect(() => {
    if (singleMedicalPaOrder?.auth_on_file?.auth_status && authStatusOptions) {
      const defaultAuthStatus = authStatusOptions?.find(
        (option) =>
          option.id === singleMedicalPaOrder?.auth_on_file?.auth_status,
      );
      if (defaultAuthStatus) {
        setSelectedAuthStatus(defaultAuthStatus);
        setOldAuthStatus(singleMedicalPaOrder.auth_on_file.auth_status);
      }
    }

    const globalBoValue = getItemFromLocalStorage(
      `${LocalStorageKeys.GLOBAL_BO_VALUE}_${singleMedicalPaOrder?.id}`,
    );

    const defaultBoValue = transformedBoOptions.find(
      (option) => option.id === globalBoValue,
    );
    setSelectedBoValue(defaultBoValue || null);
    setOldBoValue(singleMedicalPaOrder?.status?.bo_status || "");
  }, [singleMedicalPaOrder, transformedBoOptions, authStatusOptions]);

  useEffect(() => {
    if (singleMedicalPaOrder) {
      !isEventLogged.current &&
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: getOrgIdForFetchExternalWorklist() ?? "",
          event_name: EventsName.MODAL_SHOWN,
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          },
        });
      isEventLogged.current = true;
    }
  }, [singleMedicalPaOrder]);

  // Handle save button click
  const handleSave = async () => {
    const writeBackData =
      generateWriteBackDataForManualUpdate(singleMedicalPaOrder);
    setIsLoading(true);
    try {
      const payload = {
        radio_btn_flipped: radioButtonsFlipped,
        bo_value_flipped: boNotesAdded,
        radio_btn_change: {
          old_value: oldAuthStatus,
          new_value: selectedAuthStatus?.id ?? oldAuthStatus,
        },
        bo_value_change: {
          old_value: oldBoValue,
          new_value: selectedBoValue?.id ?? oldBoValue,
        },
        comments: comments,
        updated_at: new Date().toISOString(),
        updated_by: user?.email || "",
      };

      if (singleMedicalPaOrder?.id) {
        await FirestoreService.updateDocument(
          FirestoreCollectionReference.medicalPaOrders(),
          singleMedicalPaOrder.id,
          {
            mark_as_completed_trial: [
              ...(singleMedicalPaOrder.mark_as_completed_trial || []),
              payload,
            ],
            "status.mark_as_completed": true,
            "status.bo_status": selectedBoValue?.id || "",
            "auth_on_file.auth_status": selectedAuthStatus?.id || "",
            "auth_on_file.reports":
              shouldManuallyUploadCigNotes && writeBackData
                ? writeBackData
                : singleMedicalPaOrder?.auth_on_file?.reports,
          },
        );

        const boStatus = shouldManuallyUploadCigNotes
          ? selectedBoValue?.id
          : singleMedicalPaOrder?.status?.bo_status;

        const planName =
          singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "";

        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: getOrgIdForFetchExternalWorklist() ?? "",
          event_name:
            EventsName.MARK_AS_COMPLETED_BUTTON_SAVED +
            "(" +
            selectedAuthStatus?.text +
            " / " +
            boStatus +
            ")",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
            payer_name: payerName,
            plan_name: planName,
            j_code: jCode,
            master_auth_status_old: oldAuthStatusFromContext,
            master_auth_status_new:
              singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
          },
        });

        const allJCodes = getAllJCodes(singleMedicalPaOrder ?? {});
        if (allJCodes) {
          logJCodeStatusEvents(allJCodes, 100);
        }

        const parentTable = getItemFromLocalStorage(
          LocalStorageKeys.PARENT_TABLE,
        );
        if (parentTable) {
          navigate(`/worklists/${parentTable}`);
        }
        controlToastState(`mark-as-completed-trial-updated`);
        props.onClose();
      } else {
        controlToastState(`mark-as-completed-trial-updated-error`);
      }
    } catch (error) {
      controlToastState(`mark-as-completed-trial-updated-error`);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if at least one checkbox is selected
  const isBothCheckboxSelected = radioButtonsFlipped && boNotesAdded;

  // Check if any dropdown has a selected value that's different from the default
  const isAnyDropdownSelected =
    (selectedBoValue && selectedBoValue.id !== oldBoValue) ||
    (selectedAuthStatus && selectedAuthStatus.id !== oldAuthStatus);

  return (
    <Modal
      dialogId={ModalId.BO_VALUES_MODAL}
      title="Notes Confirmation"
      saveButtonText={isLoading ? "Saving..." : "Mark as completed"}
      cancelText="Cancel"
      onClose={props.onClose}
      onCancel={() => {}}
      onSave={handleSave}
      disableSave={!isBothCheckboxSelected || isLoading}
    >
      <div className="flex flex-col gap-3 p-2 text-sm">
        <div>Select the actions that have taken</div>
        <div className="gap2 flex flex-row">
          <CheckBox
            label="Radio Buttons Flipped"
            id="value1"
            defaultValue={radioButtonsFlipped}
            onCheckBoxValueChange={(data) => {
              setRadioButtonsFlipped(data.value);
              apiWorkerManager.enqueueAndProcess({
                patient_id: singleMedicalPaOrder?.patient_id ?? "",
                order_id: singleMedicalPaOrder?.order_id ?? "",
                org_id: getOrgIdForFetchExternalWorklist() ?? "",
                event_name:
                  EventsName.CHECK_BOX_CLICKED +
                  "(" +
                  "Radio Buttons Flipped" +
                  " - " +
                  data.value +
                  ")",
                user_id: user?.id ?? "",
                user_email: user?.email ?? "",
                additional_data: {
                  screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                  old_value: radioButtonsFlipped,
                  new_value: data.value,
                  payer_name: payerName,
                },
              });
            }}
          />
        </div>
        <div>
          <CheckBox
            label="BO Notes Added"
            id="value2"
            defaultValue={boNotesAdded}
            onCheckBoxValueChange={(data) => {
              setBoNotesAdded(data.value);
              apiWorkerManager.enqueueAndProcess({
                patient_id: singleMedicalPaOrder?.patient_id ?? "",
                order_id: singleMedicalPaOrder?.order_id ?? "",
                org_id: getOrgIdForFetchExternalWorklist() ?? "",
                event_name:
                  EventsName.CHECK_BOX_CLICKED +
                  "(" +
                  "BO Notes Added" +
                  " - " +
                  data.value +
                  ")",
                user_id: user?.id ?? "",
                user_email: user?.email ?? "",
                additional_data: {
                  screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                  old_value: boNotesAdded,
                  new_value: data.value,
                  payer_name: payerName,
                },
              });
            }}
          />
        </div>
        <div>
          <CheckBox
            label="Uploaded Document"
            id="value2"
            defaultValue={uploadedDocument}
            onCheckBoxValueChange={(data) => {
              setUploadedDocument(data.value);
              apiWorkerManager.enqueueAndProcess({
                patient_id: singleMedicalPaOrder?.patient_id ?? "",
                order_id: singleMedicalPaOrder?.order_id ?? "",
                org_id: getOrgIdForFetchExternalWorklist() ?? "",
                event_name:
                  EventsName.CHECK_BOX_CLICKED +
                  "(" +
                  "Uploaded Document" +
                  " - " +
                  data.value +
                  ")",
                user_id: user?.id ?? "",
                user_email: user?.email ?? "",
                additional_data: {
                  screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                  old_value: uploadedDocument,
                  new_value: data.value,
                  payer_name: payerName,
                },
              });
            }}
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <div>Confirm the global BO value : </div>
            <BadgeDropdown
              selectedBadge={selectedBoValue}
              badgeList={transformedBoOptions}
              onClick={(id) => {
                const selectedOption = transformedBoOptions.find(
                  (option) => option.id === id,
                );
                setSelectedBoValue(selectedOption || null);
              }}
            />
          </div>
        </div>
        {isAnyDropdownSelected && (
          <div className="flex flex-col gap-2 text-sm">
            <div className="text-xs">Comments</div>
            <div className="comments-section comments-textarea">
              <textarea
                className="h-20 w-full rounded border border-gray-300 p-2 text-tiny font-normal"
                placeholder="Enter your comments here"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BoValuesModal;
